import { useState, useCallback } from "react";
import { useMoney } from "../../../../hooks/useMoney";
import { useLobRegion } from "../../../../hooks/useLobRegion";
import { PrincipalFormData, PrincipalFormErrors, initialFormData, initialFormErrors, PrincipalBank } from "../types";

export const usePrincipalForm = (initialData?: Partial<PrincipalFormData>) => {
  const { code: lobCurrencyCode } = useMoney();
  const region = useLobRegion();
  const [formData, setFormData] = useState<PrincipalFormData>({
    ...initialFormData,
    codeActivationFeeCurrency: lobCurrencyCode,
    country: region.country,
    voucherType: region.indiaFields ? initialFormData.voucherType : "other",
    ...initialData,
  });

  const [formErrors, setFormErrors] = useState<PrincipalFormErrors>(initialFormErrors);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    // Handle nested fields (e.g., contactInformation.email, contactInformation.address.city)
    if (name.includes('.')) {
      const keys = name.split('.');
      
      if (keys[0] === 'contactInformation') {
        if (keys.length === 2) {
          // contactInformation.email, contactInformation.phone, etc.
          setFormData((prev) => ({
            ...prev,
            contactInformation: {
              ...prev.contactInformation,
              [keys[1]]: value,
            },
          }));
        } else if (keys.length === 3 && keys[1] === 'address') {
          // contactInformation.address.city, etc.
          setFormData((prev) => ({
            ...prev,
            contactInformation: {
              ...prev.contactInformation,
              address: {
                ...prev.contactInformation.address,
                [keys[2]]: value,
              },
            },
          }));
        }
        
        // Clear errors for nested fields
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          if (keys.length === 2) {
            newErrors.contactInformation = {
              ...prev.contactInformation,
              [keys[1]]: "",
            };
          } else if (keys.length === 3 && keys[1] === 'address') {
            newErrors.contactInformation = {
              ...prev.contactInformation,
              address: {
                ...prev.contactInformation.address,
                [keys[2]]: "",
              },
            };
          }
          return newErrors;
        });
        return;
      }
    }

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        ...(name === "addSelfDistributor" && !checked ? { distributorId: "" } : {}),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    setFormErrors((prev) => ({
      ...prev,
      [name]: typeof prev[name as keyof PrincipalFormErrors] === 'object' 
        ? prev[name as keyof PrincipalFormErrors] 
        : "",
    }));
  }, []);

  const handleAddCommissionSlab = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      commissionSlabs: [
        ...prev.commissionSlabs,
        {
          id: Date.now().toString(),
          range: "",
          percentage: "",
        },
      ],
    }));
  }, []);

  const handleRemoveCommissionSlab = useCallback((id: string) => {
    setFormData((prev) => ({
      ...prev,
      commissionSlabs: prev.commissionSlabs.filter((slab) => slab.id !== id),
    }));
  }, []);

  const handleCommissionSlabChange = useCallback((id: string, field: "range" | "percentage", value: string) => {
    setFormData((prev) => ({
      ...prev,
      commissionSlabs: prev.commissionSlabs.map((slab) =>
        slab.id === id ? { ...slab, [field]: value } : slab
      ),
    }));
  }, []);

  const validateCommissionSlabs = useCallback((): Record<string, number> | null => {
    const slabs: Record<string, number> = {};
    
    for (const slab of formData.commissionSlabs) {
      if (!slab.range || !slab.percentage) {
        return null;
      }
      const percentage = parseFloat(slab.percentage);
      if (isNaN(percentage) || percentage < 0 || percentage > 100) {
        return null;
      }
      slabs[slab.range] = percentage;
    }
    
    return Object.keys(slabs).length > 0 ? slabs : null;
  }, [formData.commissionSlabs]);

  // Whitelisted IPs handlers
  const handleAddIp = useCallback((ip: string) => {
    setFormData((prev) => ({
      ...prev,
      whiteListedIps: [...prev.whiteListedIps, ip],
    }));
  }, []);

  const handleRemoveIp = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      whiteListedIps: prev.whiteListedIps.filter((_, i) => i !== index),
    }));
  }, []);

  // Bank Accounts handlers
  const handleAddBankAccount = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      principalBanks: [
        ...prev.principalBanks,
        {
          id: Date.now().toString(),
          name: "",
          accountHolderName: "",
          accountNo: "",
          ifscCode: "",
          bankCity: "",
        },
      ],
    }));
  }, []);

  const handleRemoveBankAccount = useCallback((id: string) => {
    setFormData((prev) => ({
      ...prev,
      principalBanks: prev.principalBanks.filter((account) => account.id !== id),
    }));
  }, []);

  const handleBankAccountChange = useCallback((id: string, field: keyof PrincipalBank, value: string) => {
    setFormData((prev) => ({
      ...prev,
      principalBanks: prev.principalBanks.map((account) =>
        account.id === id ? { ...account, [field]: value } : account
      ),
    }));
  }, []);

  // File upload handlers with 5MB size validation
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  const handleLogoChange = useCallback((file: File | null, setError?: (error: string) => void) => {
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        const errorMsg = "Logo file size must be less than 5MB";
        setFormErrors((prev) => ({
          ...prev,
          logoUrl: errorMsg,
        }));
        if (setError) setError(errorMsg);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        logoUrl: file,
      }));
      setFormErrors((prev) => ({
        ...prev,
        logoUrl: "",
      }));
    }
  }, []);

  const handleContractDocumentChange = useCallback((file: File | null, setError?: (error: string) => void) => {
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        const errorMsg = "Contract document file size must be less than 5MB";
        setFormErrors((prev) => ({
          ...prev,
          contractDocumentUrl: errorMsg,
        }));
        if (setError) setError(errorMsg);
        return;
      }
      setFormData((prev) => ({
        ...prev,
        contractDocumentUrl: file,
      }));
      setFormErrors((prev) => ({
        ...prev,
        contractDocumentUrl: "",
      }));
    }
  }, []);

  const handleRemoveLogo = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      logoUrl: null,
    }));
  }, []);

  const handleRemoveContractDocument = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      contractDocumentUrl: null,
    }));
  }, []);

  const setFormDataValue = useCallback((data: Partial<PrincipalFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
  }, []);

  const resetFormErrors = useCallback(() => {
    setFormErrors(initialFormErrors);
  }, []);

  return {
    formData,
    formErrors,
    setFormErrors,
    handleChange,
    handleAddCommissionSlab,
    handleRemoveCommissionSlab,
    handleCommissionSlabChange,
    validateCommissionSlabs,
    handleAddIp,
    handleRemoveIp,
    handleAddBankAccount,
    handleRemoveBankAccount,
    handleBankAccountChange,
    handleLogoChange,
    handleContractDocumentChange,
    handleRemoveLogo,
    handleRemoveContractDocument,
    setFormDataValue,
    resetFormErrors,
  };
};
